import { APP_RECORD_TYPES, Project } from "vmd-types";

import { filterReportingItemsByRecordType } from "../../utils/filter-reporting-items-by-record-type";

export type DistributionChannelOeMatrixRow = {
  channelId: string;
  channelLabel: string;
  total: number;
  countsByOeId: Record<string, number>;
};

export type DistributionChannelOeMatrixData = {
  rows: DistributionChannelOeMatrixRow[];
  oes: { id: string; label: string }[];
  columnTotals: Record<string, number>;
  grandTotal: number;
};

/**
 * Rows are every configured distribution channel (in the order defined by
 * `distributionChannelDefinition`), columns are every OE (project) — the
 * matrix used by the "Business Apps per Distribution Channel across OEs"
 * heat map on the admin overview page (#2608).
 *
 * Business-app filtering (record-type matching + channel-header exclusion)
 * is delegated to `filterReportingItemsByRecordType`, the same helper
 * `business-app-numbers.ts` uses, so this stays in sync with the rest of the
 * dashboard instead of re-implementing its own (unnormalized) comparison.
 */
export const getDistributionChannelOeMatrixData = (
  projects: Project[] | undefined,
  distributionChannelDefinition?: { id: string; label: string }[]
): DistributionChannelOeMatrixData => {
  const oes = (projects ?? []).map((project) => ({
    id: project.id,
    label: project.customId || project.name || project.id,
  }));

  // Single pass: channelId -> oeId -> business-app count.
  const countsByChannelId = new Map<string, Map<string, number>>();

  for (const project of projects ?? []) {
    const businessApps = filterReportingItemsByRecordType(
      project.reportingItems,
      APP_RECORD_TYPES.BUSINESS_APPLICATION
    );

    for (const item of businessApps) {
      for (const channelId of item.distributionChannel ?? []) {
        const countsByOeId =
          countsByChannelId.get(channelId) ?? new Map<string, number>();
        countsByOeId.set(project.id, (countsByOeId.get(project.id) ?? 0) + 1);
        countsByChannelId.set(channelId, countsByOeId);
      }
    }
  }

  const rows = (distributionChannelDefinition ?? []).map((channel) => {
    const countsByOeIdMap = countsByChannelId.get(channel.id);
    const countsByOeId: Record<string, number> = Object.fromEntries(
      oes.map((oe) => [oe.id, countsByOeIdMap?.get(oe.id) ?? 0])
    );

    const total = Object.values(countsByOeId).reduce(
      (sum, count) => sum + count,
      0
    );

    return {
      channelId: channel.id,
      channelLabel: channel.label,
      total,
      countsByOeId,
    };
  });

  const columnTotals: Record<string, number> = Object.fromEntries(
    oes.map((oe) => [oe.id, 0])
  );
  for (const row of rows) {
    for (const [oeId, count] of Object.entries(row.countsByOeId)) {
      columnTotals[oeId] = (columnTotals[oeId] ?? 0) + count;
    }
  }

  const grandTotal = (projects ?? []).reduce(
    (sum, project) =>
      sum +
      filterReportingItemsByRecordType(
        project.reportingItems,
        APP_RECORD_TYPES.BUSINESS_APPLICATION
      ).length,
    0
  );

  return { rows, oes, columnTotals, grandTotal };
};
